import { useState, useEffect } from "react";
import { Phone, Menu, X, ArrowUpRight, Check, MapPin } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { ProjectSequence } from "../components/ProjectSequence";
import { ContactForm } from "../components/ContactForm";
import {
  PHONE_DISPLAY, PHONE_TEL, EMAIL, services,
  projects, reasons, strengths,
  shortQuotes, videoQuote, credentials, navLinks,
} from "../data/site";

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`px-6 sm:px-10 lg:px-16 ${className}`}>
    <div className="mx-auto w-full max-w-6xl">{children}</div>
  </section>
);

const Eyebrow = ({ children, num }) => (
  <div className="mb-6 flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">
    {num && <span>{num}</span>}
    <span className="h-px w-8 bg-border" />
    <span>{children}</span>
  </div>
);

const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "border-b border-border bg-white/90 backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4 sm:px-10 lg:px-16">
        <a href="#top" data-testid="nav-logo" className="text-sm font-semibold tracking-tight text-charcoal">
          Bruce Hawthorne
        </a>
        <nav className="hidden items-center gap-8 md:flex">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
              className="text-sm text-muted-foreground transition-colors hover:text-charcoal"
            >
              {l.label}
            </a>
          ))}
          <a
            href={PHONE_TEL}
            data-testid="nav-phone"
            className="inline-flex items-center gap-2 bg-charcoal px-4 py-2 text-sm text-white transition-colors hover:bg-[#333]"
          >
            <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
          </a>
        </nav>
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="text-charcoal md:hidden"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-border bg-white px-6 py-4 md:hidden">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block py-3 text-base text-charcoal"
            >
              {l.label}
            </a>
          ))}
          <a
            href={PHONE_TEL}
            data-testid="nav-mobile-phone"
            className="mt-2 inline-flex items-center gap-2 bg-charcoal px-4 py-3 text-base text-white"
          >
            <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
          </a>
        </div>
      )}
    </header>
  );
};

const Hero = () => (
  <Section id="top" className="pt-36 pb-20 sm:pt-44 sm:pb-28">
    <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-16">
      <Reveal className="lg:col-span-7">
        <p className="mb-6 text-xs uppercase tracking-[0.22em] text-muted-foreground">
          Senior Builder · Hibiscus Coast · 25+ years
        </p>
        <h1 className="text-5xl font-light leading-[1.05] tracking-tight text-charcoal sm:text-6xl lg:text-7xl">
          Bruce Hawthorne.
          <br />
          <span className="font-medium">Qualified Builder.</span>
          <br />
          Ready to start now.
        </h1>
        <p className="mt-8 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
          A senior pair of hands you can trust for full-time or subcontract, renovation
          work for building companies north of the bridge.
        </p>
        <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
          <a
            href="#contact"
            data-testid="hero-get-in-touch"
            className="inline-flex items-center justify-center gap-2 bg-charcoal px-8 py-4 text-sm uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#333]"
          >
            Get in touch <ArrowUpRight className="h-4 w-4" />
          </a>
          <a
            href={PHONE_TEL}
            data-testid="hero-phone"
            className="inline-flex items-center justify-center gap-2 border border-charcoal px-8 py-4 text-sm uppercase tracking-[0.16em] text-charcoal transition-colors hover:bg-charcoal hover:text-white"
          >
            <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
          </a>
        </div>
      </Reveal>
      <Reveal delay={0.15} className="lg:col-span-5">
        <div className="aspect-[4/5] w-full overflow-hidden bg-secondary">
          <img
            src="https://customer-assets.emergentagent.com/job_company-14/artifacts/ss7xag4p_bruce%20hero.JPG"
            alt="Bruce Hawthorne by the coast"
            className="h-full w-full object-cover"
            style={{ objectPosition: "50% 30%" }}
          />
        </div>
      </Reveal>
    </div>
  </Section>
);

const Skills = () => null;

const WhoIAm = () => (
  <Section id="about" className="py-20 sm:py-28">
    <Reveal>
      <Eyebrow num="01">Who I am</Eyebrow>
      <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-12">
        <div className="space-y-6 text-xl font-light leading-relaxed text-charcoal sm:text-2xl lg:col-span-7">
          <p>
            I've been building for over 25 years across residential and commercial work.
            I'm an NZQA-qualified carpenter, and renovations are where I do my best work.
          </p>
          <p className="text-lg text-muted-foreground sm:text-xl">
            I've run my own jobs, managed sites, mentored younger builders, and worked
            alongside plenty of good crews. These days, I'm not trying to build a company
            of my own. I'd rather turn up, help run the job properly, and leave knowing
            it's sorted.
          </p>
          <p className="text-lg text-muted-foreground sm:text-xl">
            If you're a building company that needs a senior pair of hands you can
            actually trust on site, that's me. Full-time, part-time, or a trial run to
            see how it fits.
          </p>
        </div>
        <div className="lg:col-span-5">
          <div className="border-t border-charcoal pt-6">
            <p className="text-2xl font-light leading-relaxed tracking-tight text-charcoal sm:text-[1.75rem]">
              "I'm comfortable taking responsibility for running a site. But if you need
              a senior builder to slot into the crew, get stuck in and help keep the job
              moving, I'm just as happy there too."
            </p>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground">
              No ego. Just doing what's best for the job, the team and the client.
            </p>
          </div>
        </div>
      </div>
    </Reveal>
  </Section>
);

const WhatIDo = () => (
  <Section id="services" className="py-20 sm:py-28">
    <div className="grid gap-12 lg:grid-cols-12">
      <Reveal className="lg:col-span-4">
        <Eyebrow num="02">What I do</Eyebrow>
        <p className="text-2xl font-light leading-snug tracking-tight text-charcoal sm:text-3xl">
          Renovations are where I'm strongest, but I've got depth across the whole
          build.
        </p>
        <p className="mt-6 text-base leading-relaxed text-muted-foreground">
          I know every trade well enough to keep a job moving, and I know when to
          hand off to the specialist. Where I'm not the right person, I'll make sure
          the right person is doing it.
        </p>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-8">
        <ul className="grid gap-px overflow-hidden border border-border bg-border sm:grid-cols-2">
          {services.map((s) => (
            <li key={s} className="flex items-start gap-3 bg-white px-5 py-5">
              <Check className="mt-1 h-4 w-4 shrink-0 text-charcoal" />
              <span className="text-base text-charcoal">{s}</span>
            </li>
          ))}
        </ul>
      </Reveal>
    </div>
    <Reveal className="mt-14">
      <p className="mb-6 text-xs uppercase tracking-[0.22em] text-muted-foreground">
        Indoor–outdoor deck · sketch to finish
      </p>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        {[
          { label: "Sketch", url: "https://customer-assets.emergentagent.com/job_company-14/artifacts/yry0dafa_sketch.JPG" },
          { label: "Build", url: "https://customer-assets.emergentagent.com/job_company-14/artifacts/3ej23vm9_build.webp" },
          { label: "Finish", url: "https://customer-assets.emergentagent.com/job_company-14/artifacts/8afczc1i_finish.JPG" },
        ].map((d) => (
          <div key={d.label} data-testid={`deck-${d.label.toLowerCase()}`}>
            <div className="relative aspect-[4/3] w-full overflow-hidden bg-secondary">
              <img
                src={d.url}
                alt={`Indoor–outdoor deck — ${d.label}`}
                loading="lazy"
                className="h-full w-full object-cover"
              />
              <div className="absolute left-0 top-0 m-3 bg-white/70 px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] text-charcoal/70 backdrop-blur-sm">
                {d.label}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Reveal>
  </Section>
);

const Problems = () => (
  <Section id="problems" className="py-20 sm:py-28">
    <Reveal>
      <Eyebrow num="04">Spotting problems, and solving them</Eyebrow>
    </Reveal>
    <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
      <Reveal className="lg:col-span-5">
        <div className="space-y-6 text-lg leading-relaxed text-charcoal sm:text-xl">
          <p>
            I've got a knack for spotting problems and, if required by the main
            contractor, a way of resolving them. Design issues, engineering,
            compliance, the lot.
          </p>
          <p className="text-muted-foreground">
            On the compliance side, I've got a solid understanding of what needs
            consenting and what doesn't. That way I can keep a job moving, saving the
            client money, while flagging early the things that genuinely need sign-off,
            so no nasty surprises at inspection time.
          </p>
          <p className="text-muted-foreground">
            When you're renovating, you can only x-ray vision so much when you pull
            things apart and find what the last builder left you. That's where my
            experience earns its keep. Seamlessly blending old to new is the ultimate
            result.
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.12} className="lg:col-span-7">
        <div className="relative w-full">
          <img
            src="https://customer-assets.emergentagent.com/job_company-14/artifacts/jg0b06nq_onehunga_evidence_notext%20%281%29.jpg"
            alt="Bruce's marked-up plan spotting a width that was too narrow, beside before and after photos of the house"
            loading="lazy"
            className="h-auto w-full"
          />
          <span
            data-testid="evidence-label-problem"
            className="absolute left-[3%] top-[3%] bg-white/70 px-2 py-0.5 text-[9px] uppercase tracking-[0.12em] text-charcoal/70 backdrop-blur-sm sm:text-[10px]"
          >
            Spotted the problem
          </span>
          <span
            data-testid="evidence-label-before"
            className="absolute left-[56%] top-[5%] bg-white/70 px-2 py-0.5 text-[9px] uppercase tracking-[0.12em] text-charcoal/70 backdrop-blur-sm sm:text-[10px]"
          >
            Before
          </span>
          <span
            data-testid="evidence-label-after"
            className="absolute left-[56%] top-[55%] bg-white/70 px-2 py-0.5 text-[9px] uppercase tracking-[0.12em] text-charcoal/70 backdrop-blur-sm sm:text-[10px]"
          >
            After
          </span>
        </div>
        <p className="mt-5 text-base italic leading-relaxed text-muted-foreground">
          "The plans had a width too narrow. I picked it up before we started, not after."
        </p>
      </Reveal>
    </div>
  </Section>
);

const Work = () => (
  <Section id="work" className="py-20 sm:py-28">
    <Reveal>
      <Eyebrow num="05">The work</Eyebrow>
      <h2 className="max-w-3xl text-3xl font-light leading-tight tracking-tight text-charcoal sm:text-4xl">
        Sometimes the client knows what they want, but they can't quite picture how it
        will all come together. My job is to help them see it, sort the details, and
        then build it right.
      </h2>
    </Reveal>
    <div className="mt-16 grid gap-x-10 gap-y-14 sm:grid-cols-2">
      {projects.map((p, i) => (
        <Reveal key={p.id} delay={(i % 2) * 0.08}>
          <ProjectSequence project={p} />
        </Reveal>
      ))}
    </div>
  </Section>
);

const WhyHireMe = () => (
  <Section id="why" className="py-20 sm:py-28">
    <Reveal>
      <Eyebrow num="06">Why hire me</Eyebrow>
    </Reveal>
    <div className="grid gap-px overflow-hidden border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
      {reasons.map((r, i) => (
        <Reveal key={r.title} delay={(i % 3) * 0.08} className="bg-white">
          <div className="h-full px-7 py-9">
            <h3 className="text-lg font-medium tracking-tight text-charcoal">{r.title}</h3>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">{r.body}</p>
          </div>
        </Reveal>
      ))}
    </div>
    <Reveal className="mt-8">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {strengths.map((s) => (
          <span
            key={s}
            data-testid={`strength-${s.toLowerCase().replace(/\s+/g, "-")}`}
            className="flex items-center justify-center border border-border px-4 py-4 text-center text-sm text-charcoal"
          >
            {s}
          </span>
        ))}
      </div>
    </Reveal>
  </Section>
);

const Testimonials = () => (
  <Section id="testimonials" className="py-20 sm:py-28">
    <Reveal>
      <Eyebrow num="03">What people say</Eyebrow>
    </Reveal>
    <div className="grid gap-6 sm:grid-cols-3">
      {shortQuotes.map((t, i) => (
        <Reveal key={i} delay={i * 0.08} className={i === 1 ? "sm:col-span-2" : ""}>
          <blockquote className="flex h-full flex-col justify-between border border-border p-7">
            <p className="text-base font-light leading-relaxed text-charcoal sm:text-lg">
              "{t.quote}"
            </p>
            <footer className="mt-6 text-sm text-muted-foreground">— {t.author}</footer>
          </blockquote>
        </Reveal>
      ))}
    </div>
    <div className="mt-12 grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
      <Reveal>
        <div className="relative w-full overflow-hidden bg-secondary" style={{ paddingTop: "56.25%" }}>
          <iframe
            data-testid="testimonial-video"
            className="absolute inset-0 h-full w-full"
            src="https://www.youtube.com/embed/FTHoEu3SECo"
            title="Hear from a Create Renovations client about working with Bruce"
            frameBorder="0"
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          Filmed for Create Renovations, where Bruce ran the build. The clients say it
          better than he would.
        </p>
      </Reveal>
      <Reveal delay={0.12}>
        <blockquote className="border-l-2 border-charcoal pl-6">
          <div className="space-y-4">
            {videoQuote.paragraphs.map((p, i) => (
              <p
                key={i}
                className="text-base font-light leading-relaxed text-charcoal sm:text-lg"
              >
                {i === 0 ? `"${p}` : p}
                {i === videoQuote.paragraphs.length - 1 ? '"' : ""}
              </p>
            ))}
          </div>
          <footer className="mt-6 text-sm text-muted-foreground">
            — Tristan & Jacqueline, Mairangi Bay
          </footer>
        </blockquote>
      </Reveal>
    </div>
  </Section>
);

const Credentials = () => (
  <Section id="credentials" className="py-20 sm:py-28">
    <div className="grid gap-10 lg:grid-cols-12">
      <Reveal className="lg:col-span-4">
        <Eyebrow num="07">Credentials</Eyebrow>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-8">
        <ul className="divide-y divide-border border-y border-border">
          {credentials.map((c) => (
            <li key={c} className="py-4 text-base text-charcoal sm:text-lg">{c}</li>
          ))}
        </ul>
      </Reveal>
    </div>
  </Section>
);

const Availability = () => (
  <Section id="availability" className="py-20 sm:py-28">
    <Reveal>
      <Eyebrow num="08">Availability</Eyebrow>
    </Reveal>
    <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-16">
      <Reveal className="lg:col-span-7">
        <div className="flex items-start gap-4">
          <MapPin className="mt-2 hidden h-6 w-6 shrink-0 text-charcoal sm:block" />
          <div>
            <p className="text-2xl font-light leading-relaxed tracking-tight text-charcoal sm:text-3xl">
              Happy to go full-time or exclusive to one builder, can start part-time so
              you can see my added value, and if I collaborate well with your company,
              let's keep it going.
            </p>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              North of the bridge through to Warkworth, ideally within about half an hour
              of my home. Well stocked van with own tools. Specialist tools hired as
              needed.
            </p>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.12} className="lg:col-span-5">
        <div className="aspect-[4/5] w-full overflow-hidden bg-secondary">
          <img
            src={`${process.env.PUBLIC_URL}/bruce_who.jpg`}
            alt="Bruce Hawthorne on site"
            loading="lazy"
            className="h-full w-full object-cover"
            style={{ objectPosition: "50% 20%" }}
          />
        </div>
      </Reveal>
    </div>
  </Section>
);

const Contact = () => (
  <Section id="contact" className="py-20 sm:py-28">
    <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
      <Reveal className="lg:col-span-5">
        <Eyebrow num="09">Get in touch</Eyebrow>
        <h2 className="text-3xl font-light leading-tight tracking-tight text-charcoal sm:text-4xl">
          If you've got work coming up, let's have a chat.
        </h2>
        <div className="mt-10 space-y-4">
          <a
            href={PHONE_TEL}
            data-testid="contact-phone"
            className="flex items-center gap-3 text-xl text-charcoal transition-colors hover:text-[#555]"
          >
            <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
          </a>
          <a
            href={`mailto:${EMAIL}`}
            data-testid="contact-email"
            className="flex items-center gap-3 text-xl text-charcoal transition-colors hover:text-[#555]"
          >
            <ArrowUpRight className="h-5 w-5" /> {EMAIL}
          </a>
        </div>
      </Reveal>
      <Reveal delay={0.12} className="lg:col-span-7">
        <ContactForm />
      </Reveal>
    </div>
  </Section>
);

const Footer = () => (
  <footer className="border-t border-border px-6 py-12 sm:px-10 lg:px-16">
    <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
      <p className="text-sm text-muted-foreground">
        Bruce Hawthorne · Qualified builder · 25+ years bringing expertise to the
        Hibiscus Coast
      </p>
      <a href={PHONE_TEL} className="text-sm text-charcoal hover:text-[#555]">
        brucebuilds.co.nz · {PHONE_DISPLAY}
      </a>
    </div>
  </footer>
);

export default function Landing() {
  return (
    <div className="bg-white">
      <Nav />
      <main>
        <Hero />
        <WhoIAm />
        <WhatIDo />
        <Testimonials />
        <Problems />
        <Work />
        <WhyHireMe />
        <Credentials />
        <Availability />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
